import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiLock, FiEye, FiEyeOff, FiShield } = FiIcons;

const PasswordProtection = ({ onAuthenticate }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate a brief loading period for better UX
    setTimeout(() => {
      if (password === 'AI25') {
        onAuthenticate(true);
        localStorage.setItem('sponsorAuthenticated', 'true');
      } else {
        setError('Invalid password. Please contact ProActuary for access.');
        setPassword('');
      }
      setIsLoading(false);
    }, 800);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-proactuary-blue-900 via-proactuary-blue-800 to-proactuary-green-900 flex items-center justify-center px-4">
      <motion.div
        className="glass-effect rounded-2xl p-8 w-full max-w-md border border-proactuary-blue-500/50"
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.6 }}
      >
        {/* ProActuary Logo */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <img
            src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1758203824297-Primary-logo-white-on-transparent.png"
            alt="ProActuary"
            className="h-12 w-auto mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-white mb-2 flex items-center justify-center">
            <SafeIcon icon={FiShield} className="text-proactuary-blue-400 mr-2" />
            Sponsor Dashboard Access
          </h1>
          <p className="text-proactuary-blue-200 text-sm">
            AI Actuary Summit 2025 - Performance Metrics
          </p>
        </motion.div>

        {/* Password Form */}
        <motion.form
          onSubmit={handleSubmit}
          className="space-y-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          <div>
            <label className="block text-white/90 text-sm font-medium mb-2">
              Sponsor Access Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-proactuary-blue-400 focus:border-transparent transition-all"
                placeholder="Enter sponsor password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/60 hover:text-white/80 transition-colors"
              >
                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} className="text-lg" />
              </button>
            </div>
          </div>

          {error && (
            <motion.div
              className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 text-red-300 text-sm"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}

          <motion.button
            type="submit"
            disabled={isLoading || !password}
            className="w-full bg-gradient-to-r from-proactuary-blue-500 to-proactuary-green-500 hover:from-proactuary-blue-600 hover:to-proactuary-green-600 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>Authenticating...</span>
              </>
            ) : (
              <>
                <SafeIcon icon={FiLock} className="text-lg" />
                <span>Access Dashboard</span>
              </>
            )}
          </motion.button>
        </motion.form>

        {/* Footer Info */}
        <motion.div
          className="mt-8 pt-6 border-t border-white/20 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
        >
          <p className="text-white/60 text-xs">
            Exclusive access for AI Actuary Summit 2025 sponsors
          </p>
          <p className="text-white/50 text-xs mt-1">
            Contact: virtualsummit@proactuary.com
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default PasswordProtection;